import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { UserX } from "lucide-react";

import { confirmedContacts, inviteErrors, checkinErrors, attending, firstName, todayKey, upcomingItems } from "@/lib/v3Plans";
import { ConnectionError } from "../components/ConnectionError";
import { Loading } from "../components/kit";
import { V3Title, V3Columns, V3Actions, DetailRow, DetailList, StatusBanner, V3_PRIMARY, V3_SECONDARY } from "../components/v3";
import { AreaCard, ResponsibleCard } from "../components/v3app";
import { InfoBox, StagePill } from "../components/v3people";
import { useConversations } from "../components/v3messages";
import { usePlanItems, useTrusted, CheckRow, CheckList, RowSelect, Blocked, FieldError } from "../components/v3plans";
import { EmergencyNote } from "./Safety";

const Aside = ({ text: body, title }) => <><AreaCard title={title} text={body} testid="trusted-area" /><ResponsibleCard /></>;
// The proposed trusted-contact list: null when the Core has no endpoint yet (404), undefined while loading.
const trustedList = (t) => (t.error === "notfound" ? null : t.data ? t.data.list : undefined);

// M96: invite one confirmed contact; sending needs POST /member/trusted-contacts/invitations (D-84), so it stays locked.
function InviteForm({ contacts, trusted }) {
  const [who, setWho] = useState("");
  const [errors, setErrors] = useState({});
  const [review, setReview] = useState(false);
  const person = contacts.find((c) => c.id === who) || null;
  const first = person ? firstName(person.name) : "";
  const choose = (v) => { setWho(v); setErrors({}); setReview(false); };
  const check = () => { const e = inviteErrors(who, contacts, trusted); setErrors(e); setReview(!Object.keys(e).length); };
  return (
    <V3Columns layout="detail" aside={<Aside title="Ausgewählter Vorgang" text={person ? person.name : "Noch keine Person ausgewählt"} />} main={<>
      <StagePill testid="trusted-stage">Vor der Entscheidung</StagePill>
      <DetailList>
        <RowSelect id="trusted-person" label="Eingeladene Person" value={who} onChange={choose} error={errors.person} testid="trusted-person">
          <option value="">Bitte bestätigten Kontakt auswählen</option>
          {contacts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </RowSelect>
        <DetailRow label="Zweck" value="Freiwillige Rückmeldung zum Treffen" testid="trusted-purpose" />
        <DetailRow label="Gewählte Daten" value="Termin und öffentlicher Treffpunkt" testid="trusted-data" />
        <DetailRow label="Zustimmung" value={first ? `Annahme durch ${first} steht noch aus` : "Annahme durch die eingeladene Person steht noch aus"} testid="trusted-consent" />
        <InfoBox title="Wirkung nach Bestätigung" text="Der Vorgang bleibt bis zu deiner ausdrücklichen Bestätigung offen." testid="trusted-effect" />
      </DetailList>
      {review && <InfoBox title="Einladung prüfen" testid="trusted-review" text={`${person.name} erhielte nur Termin und öffentlichen Treffpunkt der Treffen, die du jeweils auswählst. Keine Ortung, kein Notruf.`} />}
      {(review || trusted === null) && <Blocked id="trusted-blocked">Vertraute Kontakte sind im gemeinsamen Core noch nicht eingerichtet. Es wird keine Einladung gesendet und nichts gespeichert.</Blocked>}
      <V3Actions>
        {review ? <button type="button" disabled aria-describedby="trusted-blocked" data-testid="trusted-send" className={V3_PRIMARY}>Einladung senden</button>
          : <button type="button" onClick={check} data-testid="trusted-check" className={V3_PRIMARY}>Einladung prüfen</button>}
        {review ? <button type="button" onClick={() => setReview(false)} data-testid="trusted-edit" className={V3_SECONDARY}>Ändern</button>
          : <Link to="/sicherheit" data-testid="trusted-cancel" className={V3_SECONDARY}>Abbrechen</Link>}
      </V3Actions>
      <EmergencyNote testid="trusted-emergency" />
    </>} />
  );
}

function NoContacts() {
  return (<>
    <StatusBanner tone="sand" icon={UserX} title="Noch keine bestätigten Kontakte" text="Einladen kannst du nur Personen, deren Kontaktanfrage bestätigt ist." testid="trusted-no-contacts" />
    <V3Actions><Link to="/menschen" data-testid="trusted-find" className={V3_PRIMARY}>Menschen entdecken</Link><Link to="/sicherheit" data-testid="trusted-cancel" className={V3_SECONDARY}>Zurück</Link></V3Actions>
  </>);
}

export function TrustedInvite() {
  const conv = useConversations();
  const t = useTrusted();
  const list = trustedList(t);
  const failed = conv.error || (t.error && t.error !== "notfound");
  return (
    <div data-m="M96" data-testid="trusted-invite">
      <V3Title title="Einladung als vertrauter Kontakt" intro="Eine vertraute Person erhält nur die von dir gewählten Treffendaten." testid="trusted-title" />
      {failed ? <ConnectionError onRetry={() => { conv.reload(); t.reload(); }} /> : !conv.data || list === undefined ? <Loading />
        : confirmedContacts(conv.data).length ? <InviteForm contacts={confirmedContacts(conv.data)} trusted={list} />
        : <V3Columns layout="detail" aside={<Aside title="Ausgewählter Vorgang" text="Noch keine Person ausgewählt" />} main={<NoContacts />} />}
    </div>
  );
}

const CHOICE = { arrived: "Bin gut angekommen", plan_changed: "Mein Plan hat sich geändert" };

// M97: check-in after a confirmed meeting; sending needs POST /member/checkins (D-86), so it stays locked.
function CheckInForm({ items, trusted }) {
  const [params] = useSearchParams();
  const options = attending(items);
  const cur = options.find((i) => i.id === params.get("termin")) || upcomingItems(options, todayKey())[0] || options[options.length - 1] || null;
  const [choice, setChoice] = useState("");
  const [errors, setErrors] = useState({});
  const [review, setReview] = useState(false);
  const recipients = (trusted || []).filter((x) => x.state === "confirmed");
  const pick = (v) => () => { setChoice(v); setErrors({}); setReview(false); };
  const check = () => { const e = checkinErrors({ termin: cur?.id, choice }, items); setErrors(e); setReview(!Object.keys(e).length); };
  const to = trusted === null ? "Noch nicht verfügbar" : recipients.length ? recipients.map((x) => x.name).join(", ") : "Noch kein bestätigter vertrauter Kontakt";
  const radio = (v) => ({ type: "radio", name: "checkin-choice", value: v, onChange: pick(v), "aria-describedby": errors.choice ? "checkin-choice-error" : undefined });
  return (
    <V3Columns layout="detail" aside={<Aside text={cur ? cur.title : "Kein bestätigter Termin"} />} main={<>
      <CheckList>
        <CheckRow title="Treffen" value={cur ? cur.title : "Kein bestätigter Termin"} checked={false} testid="checkin-meeting" />
        <CheckRow title="Empfänger" value={to} checked={recipients.length > 0} testid="checkin-recipient" />
        <div role="radiogroup" aria-label="Deine Rückmeldung" className="space-y-[13px]">
          <CheckRow title="Rückmeldung" value={CHOICE.arrived} checked={choice === "arrived"} input={radio("arrived")} testid="checkin-arrived" />
          <CheckRow title="Alternative" value={CHOICE.plan_changed} checked={choice === "plan_changed"} input={radio("plan_changed")} testid="checkin-changed" />
        </div>
      </CheckList>
      <FieldError id="checkin-termin-error">{errors.termin}</FieldError>
      <FieldError id="checkin-choice-error">{errors.choice}</FieldError>
      {review && <InfoBox title="Rückmeldung prüfen" testid="checkin-review" text={`„${CHOICE[choice]}“ zu ${cur.title} an ${recipients.length ? to : "deine vertrauten Kontakte"}. Es wird kein Standort gesendet.`} />}
      {(review || trusted === null) && <Blocked id="checkin-blocked">Rückmeldungen sind im gemeinsamen Core noch nicht eingerichtet. Es wird nichts gesendet; bitte gib bei Bedarf direkt Bescheid.</Blocked>}
      <V3Actions>
        {review ? <button type="button" disabled aria-describedby="checkin-blocked" data-testid="checkin-send" className={V3_PRIMARY}>Rückmeldung senden</button>
          : <button type="button" onClick={check} data-testid="checkin-check" className={V3_PRIMARY}>Rückmeldung prüfen</button>}
        {review ? <button type="button" onClick={() => setReview(false)} data-testid="checkin-edit" className={V3_SECONDARY}>Ändern</button>
          : <Link to="/sicherheit" data-testid="checkin-back" className={V3_SECONDARY}>Zurück</Link>}
      </V3Actions>
      <EmergencyNote testid="checkin-emergency" />
    </>} />
  );
}

export function CheckIn() {
  const res = usePlanItems();
  const t = useTrusted();
  const list = trustedList(t);
  const failed = res.error || (t.error && t.error !== "notfound");
  const who = (list || []).find((x) => x.state === "confirmed");
  return (
    <div data-m="M97" data-testid="checkin-page">
      <V3Title title="Rückmeldung zum Treffen" intro={`Du entscheidest selbst, ob du ${who ? firstName(who.name) : "einer vertrauten Person"} eine Rückmeldung sendest.`} testid="checkin-title" />
      {failed ? <ConnectionError onRetry={() => { res.reload(); t.reload(); }} /> : !res.items || list === undefined ? <Loading /> : <CheckInForm items={res.items} trusted={list} />}
    </div>
  );
}
